import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy,
  Timestamp,
  increment,
  limit,
  startAfter,
  onSnapshot,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { db, handleFirestoreConnection } from '../firebase/config';
import { Post, Subscriber, Analytics, Comment } from '../types';

// Error handling wrapper for Firestore operations
const withRetry = async <T>(operation: () => Promise<T>, maxRetries = 3): Promise<T> => {
  let lastError: Error;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;
      console.warn(`Firestore operation failed (attempt ${attempt}/${maxRetries}):`, error);
      
      if (attempt < maxRetries) {
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        
        // Try to reconnect
        try {
          await handleFirestoreConnection();
        } catch (reconnectError) {
          console.warn('Failed to reconnect to Firestore:', reconnectError);
        }
      }
    }
  }
  
  throw lastError!;
};

// Posts Collection
export const postsCollection = collection(db, 'posts');

export const createPost = async (post: Omit<Post, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const now = Timestamp.now();
  const docRef = await addDoc(postsCollection, {
    ...post,
    createdAt: now,
    updatedAt: now,
  });
  return docRef.id;
};

export const updatePost = async (id: string, updates: Partial<Post>): Promise<void> => {
  const postRef = doc(db, 'posts', id);
  await updateDoc(postRef, {
    ...updates,
    updatedAt: Timestamp.now(),
  });
};

export const deletePost = async (id: string): Promise<void> => {
  const postRef = doc(db, 'posts', id);
  await deleteDoc(postRef);
};

export const getPosts = async (userId: string): Promise<Post[]> => {
  return withRetry(async () => {
    const q = query(postsCollection, where('authorId', '==', userId), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      updatedAt: doc.data().updatedAt.toDate(),
    })) as Post[];
  });
};

export const getPost = async (id: string): Promise<Post | null> => {
  const postRef = doc(db, 'posts', id);
  const postSnap = await getDoc(postRef);
  if (postSnap.exists()) {
    const data = postSnap.data();
    return {
      id: postSnap.id,
      ...data,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
    } as Post;
  }
  return null;
};

// Subscribers Collection
export const subscribersCollection = collection(db, 'subscribers');

export const createSubscriber = async (subscriber: Omit<Subscriber, 'id' | 'subscribedAt'>): Promise<string> => {
  const docRef = await addDoc(subscribersCollection, {
    ...subscriber,
    subscribedAt: Timestamp.now(),
  });
  return docRef.id;
};

export const updateSubscriber = async (id: string, updates: Partial<Subscriber>): Promise<void> => {
  const subscriberRef = doc(db, 'subscribers', id);
  await updateDoc(subscriberRef, updates);
};

export const deleteSubscriber = async (id: string): Promise<void> => {
  const subscriberRef = doc(db, 'subscribers', id);
  await deleteDoc(subscriberRef);
};

export const getSubscribers = async (userId: string): Promise<Subscriber[]> => {
  const q = query(subscribersCollection, where('userId', '==', userId), orderBy('subscribedAt', 'desc'));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    subscribedAt: doc.data().subscribedAt.toDate(),
  })) as Subscriber[];
};

// Analytics Collection
export const analyticsCollection = collection(db, 'analytics');

export const getAnalytics = async (userId: string): Promise<Analytics | null> => {
  const q = query(analyticsCollection, where('userId', '==', userId));
  const querySnapshot = await getDocs(q);
  if (!querySnapshot.empty) {
    const doc = querySnapshot.docs[0];
    return doc.data() as Analytics;
  }
  return null;
};

export const updateAnalytics = async (userId: string, analytics: Analytics): Promise<void> => {
  const q = query(analyticsCollection, where('userId', '==', userId));
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    await addDoc(analyticsCollection, { ...analytics, userId });
  } else {
    const docRef = querySnapshot.docs[0].ref;
    await updateDoc(docRef, { ...analytics, userId });
  }
};

// Subscriber management functions
export const addSubscriber = async (userId: string, subscriber: Omit<Subscriber, 'id' | 'subscribedAt'>): Promise<string> => {
  const subscriberData = {
    ...subscriber,
    userId,
    subscribedAt: new Date(),
  };
  const docRef = await addDoc(subscribersCollection, subscriberData);
  return docRef.id;
};

// Comments Collection
export const commentsCollection = collection(db, 'comments');

export const addComment = async (comment: Omit<Comment, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const commentData = {
    ...comment,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };
  const docRef = await addDoc(commentsCollection, commentData);
  return docRef.id;
};

export const getComments = async (postId: string): Promise<Comment[]> => {
  const q = query(
    commentsCollection, 
    where('postId', '==', postId),
    where('isApproved', '==', true),
    orderBy('createdAt', 'asc')
  );
  const querySnapshot = await getDocs(q);
  
  return querySnapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      postId: data.postId,
      authorName: data.authorName,
      authorEmail: data.authorEmail,
      content: data.content,
      isApproved: data.isApproved,
      parentId: data.parentId || null,
      likes: data.likes || 0,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
    };
  });
};

export const updateComment = async (commentId: string, updates: Partial<Comment>): Promise<void> => {
  const commentRef = doc(db, 'comments', commentId);
  await updateDoc(commentRef, {
    ...updates,
    updatedAt: Timestamp.now(),
  });
};

export const deleteComment = async (commentId: string): Promise<void> => {
  const commentRef = doc(db, 'comments', commentId);
  await deleteDoc(commentRef);
};

// Engagement functions
export const likePost = async (postId: string): Promise<void> => {
  const postRef = doc(db, 'posts', postId);
  await updateDoc(postRef, {
    likes: increment(1),
    updatedAt: Timestamp.now(),
  });
};

export const unlikePost = async (postId: string): Promise<void> => {
  const postRef = doc(db, 'posts', postId);
  await updateDoc(postRef, {
    likes: increment(-1),
    updatedAt: Timestamp.now(),
  });
};

export const incrementPostViews = async (postId: string): Promise<void> => {
  const postRef = doc(db, 'posts', postId);
  await updateDoc(postRef, {
    views: increment(1),
    updatedAt: Timestamp.now(),
  });
};

export const incrementPostShares = async (postId: string): Promise<void> => {
  const postRef = doc(db, 'posts', postId);
  await updateDoc(postRef, {
    shares: increment(1),
    updatedAt: Timestamp.now(),
  });
};

// Get single post by ID (for public pages)
export const getPostById = async (postId: string): Promise<Post | null> => {
  const postRef = doc(db, 'posts', postId);
  const postSnap = await getDoc(postRef);
  
  if (!postSnap.exists()) {
    return null;
  }
  
  const data = postSnap.data();
  return {
    id: postSnap.id,
    title: data.title,
    content: data.content,
    status: data.status,
    opens: data.opens || 0,
    clicks: data.clicks || 0,
    publishDate: data.publishDate,
    authorId: data.authorId,
    authorName: data.authorName,
    authorEmail: data.authorEmail,
    slug: data.slug,
    excerpt: data.excerpt,
    featuredImage: data.featuredImage,
    tags: data.tags || [],
    allowComments: data.allowComments !== false, // Default to true
    likes: data.likes || 0,
    userLiked: data.userLiked || false,
    shares: data.shares || 0,
    views: data.views || 0,
    createdAt: data.createdAt.toDate(),
    updatedAt: data.updatedAt.toDate(),
  };
};

// ===== ANALYTICS FUNCTIONS =====

// Dashboard Analytics Aggregation
export const getDashboardAnalytics = async (userId: string) => {
  const [posts, subscribers, analytics] = await Promise.all([
    getPosts(userId),
    getSubscribers(userId),
    getAnalytics(userId)
  ]);

  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  // Calculate metrics
  const totalPosts = posts.length;
  const publishedPosts = posts.filter(p => p.status === 'published').length;
  const draftPosts = posts.filter(p => p.status === 'draft').length;
  const totalViews = posts.reduce((sum, post) => sum + (post.views || 0), 0);
  const totalLikes = posts.reduce((sum, post) => sum + (post.likes || 0), 0);
  const totalShares = posts.reduce((sum, post) => sum + (post.shares || 0), 0);
  const avgEngagement = totalPosts > 0 ? ((totalLikes + totalShares) / totalPosts) : 0;

  // Recent activity (last 7 days)
  const recentPosts = posts.filter(p => p.createdAt >= sevenDaysAgo);
  const recentSubscribers = subscribers.filter(s => s.subscribedAt >= sevenDaysAgo);

  // Calculate growth rates
  const previousWeekSubscribers = subscribers.filter(s => 
    s.subscribedAt >= new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000) && 
    s.subscribedAt < sevenDaysAgo
  ).length;
  const currentWeekSubscribers = recentSubscribers.length;
  const subscriberGrowthRate = previousWeekSubscribers > 0 
    ? ((currentWeekSubscribers - previousWeekSubscribers) / previousWeekSubscribers) * 100 
    : 0;

  // Best performing post
  const bestPerformingPost = posts
    .filter(p => p.status === 'published')
    .sort((a, b) => (b.views || 0) - (a.views || 0))[0] || null;

  // Recent activity feed
  const recentActivity = [
    ...recentPosts.map(post => ({
      type: 'post_created',
      message: `Published "${post.title}"`,
      timestamp: post.createdAt,
      id: post.id
    })),
    ...recentSubscribers.map(sub => ({
      type: 'subscriber_added',
      message: `${sub.email} subscribed`,
      timestamp: sub.subscribedAt,
      id: sub.id
    }))
  ].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()).slice(0, 10);

  return {
    totalPosts,
    publishedPosts,
    draftPosts,
    totalViews,
    totalLikes,
    totalShares,
    avgEngagement,
    totalSubscribers: subscribers.length,
    subscriberGrowthRate,
    weeklyStreak: recentPosts.length,
    bestPerformingPost,
    recentActivity,
    analytics: analytics || {
      totalRevenue: 0,
      monthlyRevenue: 0,
      subscriberGrowth: 0,
      engagementRate: 0,
      openRate: 0,
      clickRate: 0
    }
  };
};

// Real-time analytics listener
export const subscribeToDashboardAnalytics = (userId: string, callback: (data: any) => void) => {
  const postsQuery = query(
    postsCollection, 
    where('authorId', '==', userId), 
    orderBy('createdAt', 'desc')
  );
  
  const subscribersQuery = query(
    subscribersCollection, 
    where('userId', '==', userId), 
    orderBy('subscribedAt', 'desc')
  );

  let postsData: Post[] = [];
  let subscribersData: Subscriber[] = [];

  const unsubscribePosts = onSnapshot(postsQuery, (snapshot) => {
    postsData = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      updatedAt: doc.data().updatedAt.toDate(),
    })) as Post[];
    
    updateAnalytics();
  });

  const unsubscribeSubscribers = onSnapshot(subscribersQuery, (snapshot) => {
    subscribersData = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      subscribedAt: doc.data().subscribedAt.toDate(),
    })) as Subscriber[];
    
    updateAnalytics();
  });

  const updateAnalytics = () => {
    if (postsData.length > 0 || subscribersData.length > 0) {
      const analytics = calculateAnalytics(postsData, subscribersData);
      callback(analytics);
    }
  };

  return () => {
    unsubscribePosts();
    unsubscribeSubscribers();
  };
};

// Helper function to calculate analytics
const calculateAnalytics = (posts: Post[], subscribers: Subscriber[]) => {
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const totalPosts = posts.length;
  const publishedPosts = posts.filter(p => p.status === 'published').length;
  const totalViews = posts.reduce((sum, post) => sum + (post.views || 0), 0);
  const totalLikes = posts.reduce((sum, post) => sum + (post.likes || 0), 0);
  const totalShares = posts.reduce((sum, post) => sum + (post.shares || 0), 0);
  const avgEngagement = totalPosts > 0 ? ((totalLikes + totalShares) / totalPosts) : 0;

  const recentPosts = posts.filter(p => p.createdAt >= sevenDaysAgo);
  const recentSubscribers = subscribers.filter(s => s.subscribedAt >= sevenDaysAgo);

  return {
    totalPosts,
    publishedPosts,
    totalViews,
    totalLikes,
    totalShares,
    avgEngagement,
    totalSubscribers: subscribers.length,
    weeklyStreak: recentPosts.length,
    recentActivity: [
      ...recentPosts.map(post => ({
        type: 'post_created',
        message: `Published "${post.title}"`,
        timestamp: post.createdAt,
        id: post.id
      })),
      ...recentSubscribers.map(sub => ({
        type: 'subscriber_added',
        message: `${sub.email} subscribed`,
        timestamp: sub.subscribedAt,
        id: sub.id
      }))
    ].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()).slice(0, 10)
  };
};

// Post analytics tracking
export const trackPostView = async (postId: string): Promise<void> => {
  const postRef = doc(db, 'posts', postId);
  await updateDoc(postRef, {
    views: increment(1),
    lastViewedAt: serverTimestamp()
  });
};

export const trackPostClick = async (postId: string): Promise<void> => {
  const postRef = doc(db, 'posts', postId);
  await updateDoc(postRef, {
    clicks: increment(1),
    lastClickedAt: serverTimestamp()
  });
};

export const trackPostOpen = async (postId: string): Promise<void> => {
  const postRef = doc(db, 'posts', postId);
  await updateDoc(postRef, {
    opens: increment(1),
    lastOpenedAt: serverTimestamp()
  });
};

// ===== NEW COLLECTIONS =====

// Email Templates Collection
export const emailTemplatesCollection = collection(db, 'email_templates');

export const createEmailTemplate = async (template: any): Promise<string> => {
  const docRef = await addDoc(emailTemplatesCollection, {
    ...template,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
};

export const getEmailTemplates = async (userId: string) => {
  const q = query(emailTemplatesCollection, where('userId', '==', userId), orderBy('createdAt', 'desc'));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: doc.data().createdAt?.toDate(),
    updatedAt: doc.data().updatedAt?.toDate()
  }));
};

// Media Collection
export const mediaCollection = collection(db, 'media');

export const createMediaItem = async (mediaItem: any): Promise<string> => {
  const docRef = await addDoc(mediaCollection, {
    ...mediaItem,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
};

export const getMediaItems = async (userId: string) => {
  const q = query(mediaCollection, where('userId', '==', userId), orderBy('createdAt', 'desc'));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: doc.data().createdAt?.toDate(),
    updatedAt: doc.data().updatedAt?.toDate()
  }));
};

// Segments Collection
export const segmentsCollection = collection(db, 'segments');

export const createSegment = async (segment: any): Promise<string> => {
  const docRef = await addDoc(segmentsCollection, {
    ...segment,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
};

export const getSegments = async (userId: string) => {
  const q = query(segmentsCollection, where('userId', '==', userId), orderBy('createdAt', 'desc'));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: doc.data().createdAt?.toDate(),
    updatedAt: doc.data().updatedAt?.toDate()
  }));
};

// Bulk operations for subscribers
export const bulkCreateSubscribers = async (subscribers: Omit<Subscriber, 'id' | 'subscribedAt'>[]): Promise<string[]> => {
  const batch = writeBatch(db);
  const subscriberIds: string[] = [];

  subscribers.forEach(subscriber => {
    const docRef = doc(subscribersCollection);
    batch.set(docRef, {
      ...subscriber,
      subscribedAt: serverTimestamp()
    });
    subscriberIds.push(docRef.id);
  });

  await batch.commit();
  return subscriberIds;
};

export const bulkUpdateSubscribers = async (updates: { id: string; data: Partial<Subscriber> }[]): Promise<void> => {
  const batch = writeBatch(db);

  updates.forEach(({ id, data }) => {
    const docRef = doc(subscribersCollection, id);
    batch.update(docRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
  });

  await batch.commit();
};

export const bulkDeleteSubscribers = async (subscriberIds: string[]): Promise<void> => {
  const batch = writeBatch(db);

  subscriberIds.forEach(id => {
    const docRef = doc(subscribersCollection, id);
    batch.delete(docRef);
  });

  await batch.commit();
};


